import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { Nav } from '../components/Nav';
import { Footer } from '../components/Footer';
import { CursorSpotlight } from '../components/CursorSpotlight';
import { getAllPosts } from '../lib/data';
import type { WritingTeaser } from '../lib/types';

export function EssaysIndex() {
  const [posts, setPosts] = useState<WritingTeaser[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const data = await getAllPosts();
      setPosts(data);
      setLoaded(true);
    })();
  }, []);

  /** Group by year (parsed from "2026 · 04" display format). */
  const byYear = useMemo(() => {
    const groups = new Map<string, WritingTeaser[]>();
    for (const p of posts) {
      const yr = (p.date.match(/^(\d{4})/) || [])[1] ?? '—';
      const arr = groups.get(yr) ?? [];
      arr.push(p);
      groups.set(yr, arr);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => b.localeCompare(a));
  }, [posts]);

  return (
    <div
      style={{
        background: 'var(--paper)',
        color: 'var(--ink)',
        fontFamily: 'var(--font-mono)',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        fontSize: 12,
        lineHeight: 1.55,
      }}
    >
      <CursorSpotlight />
      <TopBar />
      <Nav />
      <section className="section" style={{ padding: '56px 28px', minHeight: '60vh' }}>
        <div className="eyebrow" style={{ marginBottom: 16 }}>
          § · Writing archive · blog.danmerritts.com
        </div>
        <h1
          className="serif italic"
          style={{
            fontSize: 'var(--fs-display)',
            lineHeight: 0.95,
            letterSpacing: -1.8,
            margin: 0,
            marginBottom: 36,
          }}
        >
          All essays.
        </h1>
        {loaded && posts.length === 0 && (
          <div className="serif italic" style={{ fontSize: 18, opacity: 0.65 }}>
            Archive empty — the library is still being catalogued.
          </div>
        )}

        {loaded && posts.length > 0 && (
          <div className="eyebrow" style={{ marginBottom: 24, opacity: 0.5 }}>
            {posts.length} essays · {byYear.length} years
          </div>
        )}

        <div>
          {byYear.map(([year, yearPosts]) => (
            <section key={year} style={{ marginBottom: 48 }}>
              <div
                className="serif italic"
                style={{
                  fontSize: 48,
                  letterSpacing: -1,
                  lineHeight: 1,
                  borderBottom: '1px solid var(--ink)',
                  paddingBottom: 8,
                  marginBottom: 0,
                }}
              >
                {year}
                <span
                  className="eyebrow"
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontStyle: 'normal',
                    fontSize: 10,
                    marginLeft: 12,
                    opacity: 0.5,
                    verticalAlign: 'super',
                  }}
                >
                  {yearPosts.length}
                </span>
              </div>
              {yearPosts.map((w) => (
                <Link
                  key={w.slug}
                  to={`/essays/${w.slug}`}
                  className="writing-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '80px 1fr auto 60px',
                    gap: 24,
                    padding: '16px 0',
                    borderBottom: '1px solid var(--ink-22)',
                    alignItems: 'baseline',
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <div className="tabular" style={{ opacity: 0.55, fontSize: 11 }}>
                    {w.date}
                  </div>
                  <div
                    className="serif"
                    style={{ fontSize: 22, lineHeight: 1.2, letterSpacing: -0.2 }}
                  >
                    {w.title}
                  </div>
                  <div
                    className="serif italic writing-kicker"
                    style={{ fontSize: 14, opacity: 0.65 }}
                  >
                    {w.kicker}
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 11, opacity: 0.55 }}>
                    {w.minutes} min
                  </div>
                </Link>
              ))}
            </section>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
